import { ChangeEvent, useState, useEffect } from "react";
import SubmitSkeleton from "../SubmitSkeleton";
import { useCaseContext } from "../../../context/CaseContext";
import CustomLabel from "../../common/CustomLabel";
import { ethnicityOptions, genderOptions, referenceAge } from "../resources";
import Select from "react-select";
import { ReporterDetailsType } from "../../../types";
import SocialMediaSelector from "../SocialMediaSelector";

const ReporterDetails = ({ setStep }: { setStep: (step: number) => void }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { reporterDetails, hasAbuserInfo, updateCaseData } = useCaseContext();
  const [confirmEmail, setConfirmEmail] = useState("");

  // Initialize confirmEmail if reporterDetails.email exists
  useEffect(() => {
    if (reporterDetails?.email && !confirmEmail) {
      setConfirmEmail(reporterDetails.email);
    }
    // eslint-disable-next-line
  }, []);

  // Email validation regex
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Role validation
    if (!reporterDetails.role) {
      newErrors.role = "Please select your role";
    }

    // Non-anonymous role validations
    if (reporterDetails.role && reporterDetails.role !== "anonymous") {
      if (!reporterDetails.gender) {
        newErrors.gender = "Please select your gender";
      }

      if (!reporterDetails.age) {
        newErrors.age = "Please select your age";
      }

      if (!reporterDetails.ethnicity) {
        newErrors.ethnicity = "Please select your ethnicity";
      } else if (
        typeof reporterDetails.ethnicity === "object" &&
        reporterDetails.ethnicity.custom &&
        !reporterDetails.ethnicity.customValue
      ) {
        newErrors.ethnicityCustom = "Please specify your ethnicity";
      }
    }

    // Email validations
    if (!reporterDetails.email) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(reporterDetails.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Confirm email validations
    if (!confirmEmail) {
      newErrors.confirmEmail = "Please confirm your email";
    } else if (confirmEmail !== reporterDetails.email) {
      newErrors.confirmEmail = "Email addresses do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReporterDetailChange = (
    val: string | number | undefined,
    field: keyof ReporterDetailsType
  ) => {
    // Clear relevant error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Phone validation
    if (
      field === "phone" &&
      typeof val === "string" &&
      !/^[\d+()-.]*$/.test(val) // Allow digits, +, -, (, and )
    ) {
      return;
    }

    // Ethnicity special case
    if (field === "ethnicity" && val === "other") {
      const otherVal = { custom: true, customValue: "" };

      updateCaseData("reporterDetails", {
        ...reporterDetails,
        [field]: otherVal,
      });
    } else {
      updateCaseData("reporterDetails", {
        ...reporterDetails,
        [field]: val,
      });
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleReporterDetailChange(value, "email");

    // Clear email match error when email changes
    if (errors.confirmEmail && confirmEmail === value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.confirmEmail;
        return newErrors;
      });
    }
  };

  const handleConfirmEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setConfirmEmail(value);

    // Validate and clear confirm email error if it matches
    if (errors.confirmEmail && value === reporterDetails.email) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.confirmEmail;
        return newErrors;
      });
    }
  };

  const handleOtherEthnicityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Clear ethnicity custom error when field is updated
    if (errors.ethnicityCustom && value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ethnicityCustom;
        return newErrors;
      });
    }

    updateCaseData("reporterDetails", {
      ...reporterDetails,
      ethnicity: {
        custom: true,
        customValue: value,
      },
    });
  };

  const handleNextStep = () => {
    const isValid = validateForm();
    if (isValid) {
      hasAbuserInfo ? setStep(8) : setStep(9);
    }
  };

  const canProceed =
    reporterDetails.role &&
    (reporterDetails.role === "anonymous" ||
      (reporterDetails.role !== "anonymous" &&
        reporterDetails.gender &&
        reporterDetails.age &&
        reporterDetails.ethnicity &&
        (typeof reporterDetails.ethnicity !== "object" ||
          (typeof reporterDetails.ethnicity === "object" &&
            reporterDetails.ethnicity.customValue)))) &&
    reporterDetails.email &&
    isValidEmail(reporterDetails.email) &&
    confirmEmail &&
    reporterDetails.email === confirmEmail;

  return (
    <SubmitSkeleton
      onPrev={() => setStep(6)}
      onNext={handleNextStep}
      canProceed={!!canProceed}
      progress={7 / 10}
    >
      <div className="flex flex-col gap-4">
        {/* Role selection */}
        <div>
          <CustomLabel labelText="Are you a..." isRequired />
          <label className="flex items-center gap-3 pr-6 pl-[13px] cursor-pointer">
            <input
              type="radio"
              name="reporterRole"
              value="witness"
              checked={reporterDetails.role === "witness"}
              onChange={() => handleReporterDetailChange("witness", "role")}
              className="form-radio"
            />
            <span className="font-medium text-[14px] leading-[16.94px] text-black py-[14px]">
              Witness
            </span>
          </label>

          <label className="flex items-center gap-3 pr-6 pl-[13px] cursor-pointer">
            <input
              type="radio"
              name="reporterRole"
              value="victim"
              checked={reporterDetails.role === "victim"}
              onChange={() => handleReporterDetailChange("victim", "role")}
              className="form-radio"
            />
            <span className="font-medium text-[14px] leading-[16.94px] text-black py-[14px]">
              Victim
            </span>
          </label>

          <label className="flex items-center gap-3 pr-6 pl-[13px] cursor-pointer">
            <input
              type="radio"
              name="reporterRole"
              value="anonymous"
              checked={reporterDetails.role === "anonymous"}
              onChange={() => handleReporterDetailChange("anonymous", "role")}
              className="form-radio"
            />
            <span className="font-medium text-[14px] leading-[16.94px] text-black py-[14px]">
              I want to maintain my anonymity
            </span>
          </label>
          {errors.role && (
            <p className="text-red-500 text-sm mt-1">{errors.role}</p>
          )}
        </div>

        {reporterDetails?.role !== "anonymous" && (
          <>
            {/* Gender */}
            <CustomLabel
              labelText="Gender"
              isRequired={reporterDetails?.role !== "anonymous"}
            >
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                className="font-medium text-[14px] leading-4 cursor-pointer"
                placeholder="Select gender"
                isDisabled={reporterDetails?.role === "anonymous"}
                onChange={(selectedOption) =>
                  handleReporterDetailChange(selectedOption?.value, "gender")
                }
                defaultInputValue={reporterDetails.gender}
                options={genderOptions}
              />
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
              )}
            </CustomLabel>

            {/* Age */}
            <CustomLabel
              labelText="Age"
              isRequired={reporterDetails?.role !== "anonymous"}
            >
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                className="font-medium text-sm leading-4 cursor-pointer"
                placeholder="Select age"
                defaultInputValue={reporterDetails?.age || ""}
                onChange={(selectedOption) =>
                  handleReporterDetailChange(selectedOption?.value, "age")
                }
                options={referenceAge}
                isDisabled={reporterDetails?.role === "anonymous"}
              />
              {errors.age && (
                <p className="text-red-500 text-sm mt-1">{errors.age}</p>
              )}
            </CustomLabel>

            {/* Ethnicity */}
            <CustomLabel
              labelText="Ethnicity"
              isRequired={reporterDetails?.role !== "anonymous"}
            >
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                className="font-medium text-[14px] leading-4 cursor-pointer"
                placeholder="Select ethnicity"
                defaultInputValue={
                  typeof reporterDetails.ethnicity === "object"
                    ? "other"
                    : reporterDetails?.ethnicity || ""
                }
                onChange={(selectedOption) =>
                  handleReporterDetailChange(selectedOption?.value, "ethnicity")
                }
                options={ethnicityOptions}
                isDisabled={reporterDetails?.role === "anonymous"}
              />
              {errors.ethnicity && (
                <p className="text-red-500 text-sm mt-1">{errors.ethnicity}</p>
              )}
            </CustomLabel>

            {/* Other Ethnicity */}
            {reporterDetails?.ethnicity &&
              typeof reporterDetails.ethnicity === "object" &&
              reporterDetails.ethnicity.custom && (
                <label className="flex flex-col gap-y-2">
                  <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                    Other
                  </span>
                  <input
                    type="text"
                    value={reporterDetails.ethnicity.customValue}
                    onChange={handleOtherEthnicityChange}
                    placeholder="Please specify..."
                    className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:ring-1 focus:ring-black focus:border-transparent"
                  />
                  {errors.ethnicityCustom && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.ethnicityCustom}
                    </p>
                  )}
                </label>
              )}

            {/* Phone Number */}
            <CustomLabel labelText="Phone Number">
              <input
                type="tel"
                pattern="^[0-9\-\+\s\(\)]*$"
                placeholder="Enter phone number"
                disabled={reporterDetails?.role === "anonymous"}
                className="font-medium text-sm leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
                value={reporterDetails?.phone || ""}
                onChange={(e) =>
                  handleReporterDetailChange(e.target.value, "phone")
                }
              />
            </CustomLabel>
          </>
        )}

        {/* Email */}
        <CustomLabel labelText="E-mail" isRequired>
          <input
            type="email"
            placeholder="Enter e-mail address"
            className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
            value={reporterDetails?.email || ""}
            onChange={handleEmailChange}
          />

          <p className="font-normal text-xs leading-3 text-red-500 px-1 mt-1">
            {reporterDetails?.role === "anonymous"
              ? "Your anonymity is ensured. This is only for sending your report details."
              : "Ensure this is accurate, as your report details will be sent here."}
          </p>
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </CustomLabel>

        {/* Confirm Email */}
        <CustomLabel labelText="Confirm E-mail" isRequired>
          <input
            type="email"
            placeholder="Confirm e-mail address"
            className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
            value={confirmEmail}
            onChange={handleConfirmEmailChange}
          />
          {errors.confirmEmail && (
            <p className="text-red-500 text-sm mt-1">{errors.confirmEmail}</p>
          )}
        </CustomLabel>

        {/* Social Media Selector */}
        {reporterDetails?.role !== "anonymous" && (
          <SocialMediaSelector
            labelText="Social Media (Optional)"
            value={reporterDetails.socials}
            setValue={(updatedSocials) =>
              updateCaseData("reporterDetails", {
                ...reporterDetails,
                socials: updatedSocials,
              })
            }
            disabled={reporterDetails?.role === "anonymous"}
          />
        )}
      </div>
    </SubmitSkeleton>
  );
};

export default ReporterDetails;
